import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const styles = {
  header: { display: "flex", justifyContent: "space-between", alignItems: "center" },
  formControl: { width: "100%", marginTop: "16px" },
  sectionTitle: { color: "#1a237e", fontWeight: "bold", fontSize: "13px", borderBottom: "1px solid #e0e0e0", paddingBottom: "4px", marginTop: "16px", marginBottom: "4px" },
  errorMsg: { color: "#f44336", fontSize: "13px" },
  searchBar: { display: "flex", gap: "12px", marginBottom: "16px", alignItems: "flex-end" }
};

const QUALIFICATIONS = ["Certificate", "Diploma", "Bachelor's", "Postgraduate Diploma", "Master's", "PhD"];
const SPECIALIZATIONS = ["Education", "Business Studies", "Computer Studies", "Science", "Arts", "Languages", "Other"];
const GENDERS = ["Male", "Female"];
const CONTRACT_TYPES = ["Permanent", "Contract", "Part-time", "Visiting"];

const BLANK = {
  firstName: "", lastName: "", staffNo: "", qualification: "", specialization: "",
  gender: "", dob: "", email: "", phone: "", nationalId: "", joinDate: "",
  contractType: "Permanent", status: "active"
};

class LecturerList extends Component {
  state = {
    tab: 0,
    lecturers: [],
    loading: true,
    search: "",
    open: false,
    editing: null,
    form: { ...BLANK },
    saving: false,
    error: null
  };

  componentDidMount() { this.fetchLecturers(); }

  fetchLecturers = async () => {
    this.setState({ loading: true });
    try {
      const params = { limit: 100 };
      if (this.state.search) params.search = this.state.search;
      const res = await api.get("/lecturers", { params });
      const d = res.data.data || res.data;
      this.setState({ lecturers: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) { this.setState({ loading: false }); }
  };

  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.firstName || !form.lastName) {
      this.setState({ error: "First name and last name are required." }); return;
    }
    this.setState({ saving: true, error: null });
    try {
      if (editing) await api.put(`/lecturers/${editing}`, form);
      else await api.post("/lecturers", form);
      this.setState({ open: false, saving: false });
      this.fetchLecturers();
    } catch (e) {
      this.setState({ error: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleToggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    if (!window.confirm(`${newStatus === "active" ? "Activate" : "Deactivate"} this lecturer?`)) return;
    try {
      await api.patch(`/lecturers/${id}`, { status: newStatus });
      this.fetchLecturers();
    } catch (e) { /* silent */ }
  };

  openEdit = (l) => {
    this.setState({
      open: true, editing: l.id, error: null,
      form: {
        firstName: l.firstName || "",
        lastName: l.lastName || "",
        staffNo: l.staffNo || "",
        qualification: l.qualification || "",
        specialization: l.specialization || "",
        gender: l.gender || "",
        dob: l.dob || "",
        email: l.email || "",
        phone: l.phone || "",
        nationalId: l.nationalId || "",
        joinDate: l.joinDate || "",
        contractType: l.contractType || "Permanent",
        status: l.status || "active"
      }
    });
  };

  render() {
    const { classes, history } = this.props;
    const { lecturers, loading, search, open, editing, form, saving, error } = this.state;

    const columns = [
      { id: "staffNo", header: "Staff No", accessorFn: l => l.staffNo || "—", meta: { variant: "mono" } },
      { id: "name", header: "Name", accessorFn: l => `${l.firstName || ""} ${l.lastName || ""}`.trim() },
      { id: "qualification", header: "Qualification", accessorFn: l => l.qualification || "—" },
      { id: "specialization", header: "Specialization", accessorFn: l => l.specialization || "—" },
      { id: "contract", header: "Contract", accessorFn: l => l.contractType || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => {
          const l = info.row.original;
          return <Pill tone={l.status === "active" ? "success" : "muted"}>{l.status || "active"}</Pill>;
        }
      },
      {
        id: "actions",
        header: "",
        cell: info => {
          const l = info.row.original;
          return (
            <div style={{ display: "flex", gap: "4px" }}>
              <Button size="sm" color="info" simple onClick={() => this.openEdit(l)}>Edit</Button>
              <Button size="sm" color="primary" simple onClick={() => history.push(`/bwillis/lecturers/${l.id}/courses`)}>Courses</Button>
              <Button size="sm" color={l.status === "active" ? "danger" : "success"} simple
                onClick={() => this.handleToggleStatus(l.id, l.status)}>
                {l.status === "active" ? "Deactivate" : "Activate"}
              </Button>
            </div>
          );
        }
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <ViewToolbar
            tabs={["All Lecturers", "Visiting"]}
            activeTab={this.state.tab || 0}
            onTabChange={tab => this.setState({ tab })}
            actions={[]}
          />
          <div className={classes.searchBar}>
            <input
              type="text" placeholder="Search by name, staff no, or specialization..."
              value={search}
              onChange={e => this.setState({ search: e.target.value })}
              onKeyDown={e => e.key === "Enter" && this.fetchLecturers()}
              style={{ flex: 1, maxWidth: "360px", padding: "8px 12px", border: "1px solid #ddd", borderRadius: "4px", fontSize: "14px" }}
            />
            <Button size="sm" onClick={this.fetchLecturers} color="primary" style={{ marginBottom: "2px" }}>Search</Button>
          </div>

          <DataTable
            title="Lecturers"
            subtitle={`${lecturers.length} lecturer${lecturers.length !== 1 ? "s" : ""}`}
            actions={
              <Button size="sm" color="primary" onClick={() => this.setState({ open: true, editing: null, form: { ...BLANK }, error: null })}>
                + Add Lecturer
              </Button>
            }
            columns={columns}
            data={lecturers}
            isLoading={loading}
            emptyState="No lecturers found."
          />
        </GridItem>

        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="md" fullWidth>
          <DialogHeader title={editing ? "Edit Lecturer" : "Add Lecturer"} />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}

            <p className={classes.sectionTitle}>Personal Details</p>
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <CustomInput labelText="First Name *" id="lfFirstName" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.firstName, onChange: this.handleChange("firstName") }} />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput labelText="Last Name *" id="lfLastName" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.lastName, onChange: this.handleChange("lastName") }} />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Gender</InputLabel>
                  <Select value={form.gender} onChange={this.handleChange("gender")}>
                    <MenuItem value="">— Select —</MenuItem>
                    {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomDatePicker labelText="Date of Birth" id="lfDob" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.dob, onChange: this.handleChange("dob") }} />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomInput labelText="National ID" id="lfNationalId" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.nationalId, onChange: this.handleChange("nationalId") }} />
              </GridItem>
            </GridContainer>

            <p className={classes.sectionTitle}>Contact</p>
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <CustomInput labelText="Email" id="lfEmail" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.email, onChange: this.handleChange("email"), type: "email" }} />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput labelText="Phone" id="lfPhone" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.phone, onChange: this.handleChange("phone"), type: "tel" }} />
              </GridItem>
            </GridContainer>

            <p className={classes.sectionTitle}>Academic & Employment</p>
            <GridContainer>
              <GridItem xs={12} sm={4}>
                <CustomInput labelText="Staff No" id="lfStaffNo" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.staffNo, onChange: this.handleChange("staffNo"), placeholder: "auto-generated if blank" }} />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Highest Qualification</InputLabel>
                  <Select value={form.qualification} onChange={this.handleChange("qualification")}>
                    <MenuItem value="">— Select —</MenuItem>
                    {QUALIFICATIONS.map(q => <MenuItem key={q} value={q}>{q}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Specialization</InputLabel>
                  <Select value={form.specialization} onChange={this.handleChange("specialization")}>
                    <MenuItem value="">— Select —</MenuItem>
                    {SPECIALIZATIONS.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Contract Type</InputLabel>
                  <Select value={form.contractType} onChange={this.handleChange("contractType")}>
                    {CONTRACT_TYPES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomDatePicker labelText="Join Date" id="lfJoinDate" formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.joinDate, onChange: this.handleChange("joinDate") }} />
              </GridItem>
              {editing && (
                <GridItem xs={12} sm={4}>
                  <FormControl className={classes.formControl}>
                    <InputLabel>Status</InputLabel>
                    <Select value={form.status} onChange={this.handleChange("status")}>
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="inactive">Inactive</MenuItem>
                      <MenuItem value="on_leave">On Leave</MenuItem>
                    </Select>
                  </FormControl>
                </GridItem>
              )}
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (editing ? "Update" : "Add Lecturer")}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

LecturerList.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(LecturerList));
